import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Stack,
  TextField,
  IconButton,
  CircularProgress,
  Container,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Select,
  MenuItem,
  Tab,
  Tabs,
  Pagination,
  FormControl,
  InputLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "../../Api/ApiUrls";
import ViewModal from "./ViewModal";
import { styled } from "@mui/material/styles";

const SmallImage = styled("img")({
  width: "50px",
  height: "50px",
  objectFit: "cover",
  cursor: "pointer",
  borderRadius: "4px",
  "&:hover": {
    opacity: 0.8,
  },
});

const ImageModal = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const ModalImage = styled("img")({
  maxWidth: "90%",
  maxHeight: "90%",
});

const BestPlanTable = () => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true); // single source of truth
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [search, setSearch] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [changeMobileDialogOpen, setChangeMobileDialogOpen] = useState(false);
  const [newMobile, setNewMobile] = useState("");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [selectedRentalType, setSelectedRentalType] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [progressiveLoading, setProgressiveLoading] = useState(false);

  // Refs for debouncing and aborting
  const requestControllerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const latestRequestId = useRef(0);

  const rentalTypes = [
    "PLOT", "LAND", "VILLA", "APARTMENT"
  ];

  const getImageUrl = (imagePath) => `${BASE_URL}${imagePath}`;

  // Core fetch. Always resolves with { data, pagination } or null on failure.
  const fetchProperties = async (page, size, searchQuery, rentalType) => {
    try {
      if (requestControllerRef.current) {
        requestControllerRef.current.abort();
      }
      requestControllerRef.current = new AbortController();
      const signal = requestControllerRef.current.signal;

      // Add type=best-deal to filter specifically for best deals on server side
      let url = `${BASE_URL}/property/?type=best-deal&page=${page}&page_size=${size}`;
      
      if (rentalType) {
        url += `&category_name=${encodeURIComponent(rentalType)}`;
      }

      if (searchQuery) {
        url += `&search_query=${encodeURIComponent(searchQuery)}`;
      }

      const res = await axios.get(url, { signal });
      const data = res.data?.data || [];
      const pagination = res.data?.pagination || {};

      return { data, pagination };
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching properties:", error);
        toast.error("Failed to fetch properties");
      }
      return null;
    }
  };

  // Apply a successful fetch result to state, in one consistent place.
  const applyResult = (result, page) => {
    if (!result) return;
    const { data, pagination } = result;
    setProperties(data);
    setCurrentPage(pagination.current_page ?? page);
    setTotalPages(pagination.total_pages ?? 1);
    setTotalCount(pagination.total_count ?? data.length);
  };

  // Unified loader for page <= 100. Always: loading on, fetch, apply, loading off.
  const loadPage = async (page, size, searchQuery, rentalType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    const result = await fetchProperties(page, size, searchQuery, rentalType);
    if (requestId !== latestRequestId.current) return; // a newer request superseded this one
    applyResult(result, page);
    setIsLoading(false);
    setHasLoadedOnce(true);
  };

  // Progressive loader for page > 100, fetched in chunks of 100.
  const loadProgressively = async (page, size, searchQuery, rentalType) => {
    const requestId = ++latestRequestId.current;
    setIsLoading(true);
    setProgressiveLoading(true);

    const totalChunks = Math.ceil(size / 100);
    let allProps = [];
    let lastPagination = {};

    for (let i = 0; i < totalChunks; i++) {
      const result = await fetchProperties(page, 100, searchQuery, rentalType);
      if (requestId !== latestRequestId.current) return; // superseded mid-flight
      if (result) {
        allProps = [...allProps, ...result.data];
        lastPagination = result.pagination;
        setProperties([...allProps]);
        if (i === 0) {
          // First chunk landed — table is no longer empty, drop the full-screen spinner.
          setIsLoading(false);
        }
      }
      if (i < totalChunks - 1) {
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
    }

    if (requestId !== latestRequestId.current) return;
    const recalculatedTotalPages = Math.ceil((lastPagination.total_count || 0) / size);
    setTotalPages(recalculatedTotalPages || 1);
    setTotalCount(lastPagination.total_count || allProps.length);
    setCurrentPage(page);
    setIsLoading(false);
    setProgressiveLoading(false);
    setHasLoadedOnce(true);
  };

  const runLoad = (page, size, searchQuery, rentalType) => {
    if (size > 100) {
      loadProgressively(page, size, searchQuery, rentalType);
    } else {
      loadPage(page, size, searchQuery, rentalType);
    }
  };

  // Tab switch OR page size change -> reset to page 1, reload (single effect, no duplicate fetch on mount)
  useEffect(() => {
    runLoad(1, pageSize, search, selectedRentalType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRentalType, pageSize]);

  // Search -> debounce, reload current page
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      runLoad(1, pageSize, search, selectedRentalType);
    }, 300);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleDelete = async (property_id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await axios.delete(`${BASE_URL}/property/${property_id}/`);
        toast.success("Property deleted successfully");
        runLoad(currentPage, pageSize, search, selectedRentalType);
      } catch (error) {
        console.error("Error deleting property:", error);
        toast.error("Failed to delete property. Please try again.");
      }
    }
  };

  const handleSearchChange = (event) => setSearch(event.target.value);

  const handleViewClick = (property) => {
    setCurrentProperty(property);
    setViewModalOpen(true);
  };

  const handlePageChange = (event, value) => {
    runLoad(value, pageSize, search, selectedRentalType);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(parseInt(event.target.value, 10));
  };

  const handleStatusChange = async (propertyId, newStatus) => {
    const previousStatus = properties.find(
      (prop) => prop.property_id === propertyId
    )?.status;
    const adminMobile = properties.find(
      (prop) => prop.property_id === propertyId
    )?.admin_mobile;

    const payload = [
      {
        property_id: propertyId,
        admin_mobile: adminMobile,
        Admin_status: newStatus,
      },
    ];

    try {
      await axios.put(`${BASE_URL}/properties-update/`, payload);

      setProperties((prev) =>
        prev.map((prop) =>
          prop.property_id === propertyId ? { ...prop, status: newStatus } : prop
        )
      );

      toast.success("Status updated successfully");
      runLoad(currentPage, pageSize, search, selectedRentalType);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");

      setProperties((prev) =>
        prev.map((prop) =>
          prop.property_id === propertyId
            ? { ...prop, status: previousStatus }
            : prop
        )
      );
    }
  };

  const handleSelectProperty = (propertyId) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleOpenChangeMobileDialog = () => {
    setChangeMobileDialogOpen(true);
    setNewMobile("");
  };

  const handleCloseChangeMobileDialog = () => setChangeMobileDialogOpen(false);

  const handleMobileChangeSubmit = async () => {
    if (!newMobile) {
      toast.error("Please enter a mobile number");
      return;
    }

    const payload = selectedProperties.map((propertyId) => {
      const property = properties.find((p) => p.property_id === propertyId);
      return {
        property_id: propertyId,
        admin_mobile: newMobile,
        Admin_status: property?.Admin_status || "Pending",
      };
    });

    try {
      await axios.put(`${BASE_URL}/properties-update/`, payload);
      toast.success("Mobile numbers updated successfully");
      runLoad(currentPage, pageSize, search, selectedRentalType);
      setSelectedProperties([]);
      setChangeMobileDialogOpen(false);
    } catch (error) {
      console.error("Error updating mobile numbers:", error);
      toast.error("Failed to update mobile numbers");
    }
  };

  const handleImageClick = (imageUrl, imageId, property_id) => {
    setSelectedImage(imageUrl);
    setSelectedImageId(imageId);
    setSelectedPropertyId(property_id);
    setImageModalOpen(true);
  };

  const handleDeleteImage = async () => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const formData = new FormData();
      formData.append("deleted_image_ids", selectedImageId.toString());

      const response = await fetch(`${BASE_URL}/property/${selectedPropertyId}/`, {
        method: "PUT",
        body: formData,
      });

      if (!response.ok) throw new Error(`Server responded with status ${response.status}`);

      await response.json();
      runLoad(currentPage, pageSize, search, selectedRentalType);
      setImageModalOpen(false);
      alert("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      alert(`Deletion failed: ${error.message}`);
    }
  };

  const handleCloseImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage("");
  };

  const handleRentalTypeChange = (event, newValue) => {
    setSelectedRentalType(newValue);
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box
        sx={{
          maxWidth: 1500,
          mx: "auto",
          p: 2,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h4"
          sx={{ textAlign: "center", color: "primary.main", mb: 2 }}
        >
          Best Deal Properties
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label="Search Properties"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search by name or location..."
              size="small"
              sx={{ width: 300 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Records</InputLabel>
              <Select
                value={pageSize}
                label="Records"
                onChange={handlePageSizeChange}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={30}>30</MenuItem>
                <MenuItem value={40}>40</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>

            {progressiveLoading && <CircularProgress size={20} />}
          </Box>

          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenChangeMobileDialog}
            disabled={selectedProperties.length === 0}
          >
            Change Mobile
          </Button>
        </Box>

        {/* Rental Type Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedRentalType}
            onChange={handleRentalTypeChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="rental type tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                minWidth: 'auto',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#1976d2',
              }
            }}
          >
            <Tab 
              label="All Types" 
              value="" 
              sx={{ 
                '&.Mui-selected': {
                  color: '#1976d2',
                }
              }}
            />
            {rentalTypes.map((type) => (
              <Tab 
                key={type} 
                label={type} 
                value={type}
                sx={{ 
                  '&.Mui-selected': {
                    color: '#1976d2',
                  }
                }}
              />
            ))}
          </Tabs>
        </Box>

        {isLoading && properties.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 300 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 600, overflowY: "auto" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#1976d2",
                      color: "white",
                      textAlign: "center",
                      fontSize: 16,
                      fontWeight: "bold",
                      position: "sticky",
                      top: 0,
                      zIndex: 1
                    }}
                  >
                    <strong>Select</strong>
                  </TableCell>

                  {[
                    "S.No",
                    "Prop ID",
                    "Name",
                    "Images",
                    "User Details",
                    "Admin Mobile",
                    "Posted By",
                    "Facing",
                    "Site Area",
                    "Price",
                    "Location",
                    "Status",
                    "Actions",
                  ].map((head) => (
                    <TableCell
                      key={head}
                      sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        textAlign: "center",
                        fontSize: 16,
                        position: "sticky",
                        top: 0,
                        zIndex: 1
                      }}
                    >
                      <strong>{head}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {properties.map((property, index) => (
                  <TableRow key={property.property_id}>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Checkbox
                        checked={selectedProperties.includes(property.property_id)}
                        onChange={() => handleSelectProperty(property.property_id)}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {(currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>
                      {`PROP${property.property_id}`}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.property_name}</TableCell>
                    <TableCell sx={{ textAlign: "center", width: 120 }}>
                      {property.property_images?.length > 0 ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${Math.min(property.property_images.length, 3)}, 1fr)`,
                            gap: 0.5,
                            width: "100%",
                          }}
                        >
                          {property.property_images.map((img) => {
                            const imageUrl = getImageUrl(img.image);

                            return (
                              <Box
                                key={img.id}
                                sx={{
                                  width: "100%",
                                  aspectRatio: "1 / 1",
                                  overflow: "hidden",
                                  borderRadius: 1,
                                  cursor: "pointer",
                                  border: "1px solid #ddd",
                                }}
                                onClick={() =>
                                  handleImageClick(imageUrl, img.id, property.property_id)
                                }
                              >
                                <Box
                                  component="img"
                                  src={imageUrl}
                                  alt=""
                                  onError={(e) =>
                                    (e.target.src = "https://via.placeholder.com/50")
                                  }
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Typography variant="body2">No images</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Box>
                        <Typography variant="body2">{property.user_first_name} {property.user_last_name}</Typography>
                        <Typography variant="caption" display="block">{property.user_mobile_no}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.admin_mobile}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.posted_by}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.facing}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.site_area}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.price}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>{property.location}</TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Select
                        value={property.Admin_status || "Pending"}
                        onChange={(e) => handleStatusChange(property.property_id, e.target.value)}
                        sx={{ minWidth: 120 }}
                      >
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Approved">Approved</MenuItem>
                        <MenuItem value="Rejected">Rejected</MenuItem>
                      </Select>
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <IconButton color="info" onClick={() => handleViewClick(property)}>
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDelete(property.property_id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination Controls */}
        {hasLoadedOnce && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {properties.length} of {totalCount} records
            </Typography>
            
            {totalPages > 1 && (
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
                disabled={isLoading}
              />
            )}
          </Box>
        )}
      </Box>

      {/* View Modal */}
      <ViewModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
            propertyId={currentProperty?.property_id}

      />

      {/* Change Mobile Dialog */}
      <Dialog open={changeMobileDialogOpen} onClose={handleCloseChangeMobileDialog}>
        <DialogTitle>Change Mobile Number</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Mobile Number"
            type="text"
            fullWidth
            variant="outlined"
            value={newMobile}
            onChange={(e) => setNewMobile(e.target.value)}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This will update the admin_mobile field for {selectedProperties.length} selected properties.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeMobileDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleMobileChangeSubmit} color="primary" variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Modal */}
      <ImageModal open={imageModalOpen} onClose={handleCloseImageModal}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <ModalImage
            src={selectedImage}
            alt="Enlarged property image"
            onError={(e) => (e.target.src = "https://via.placeholder.com/500")}
          />
          <IconButton
            onClick={handleDeleteImage}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              backgroundColor: "rgba(255,255,255,0.8)",
              '&:hover': { backgroundColor: "rgba(255,255,255,1)", transform: "scale(1.1)" },
              transition: "all 0.2s ease",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              '& .MuiSvgIcon-root': { fontSize: "1.5rem" },
            }}
          >
            <DeleteIcon color="error" />
          </IconButton>
        </Box>
      </ImageModal>
    </Container>
  );
};

export default BestPlanTable;